import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, User, MapPin } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuthState } from '@/hooks/useAuth'
import { AuthModal } from '@/components/auth/AuthModal'
import { db, storage } from '@/lib/firebase'
import { doc, setDoc, serverTimestamp, collection, query, where, getDocs } from 'firebase/firestore'
import { ref, getBytes } from 'firebase/storage'
import type { UnrealArtist } from '@/types/models'

interface ExternalArtist {
  city: string
  username: string
  profile_image_url: string
}

export default function CreateArtistPage() {
  const { } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, loading: authLoading } = useAuthState()
  
  const initialUsername = searchParams.get('username') || ''
  const searchQuery = searchParams.get('q') || ''
  
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [formData, setFormData] = useState({
    username: initialUsername,
    displayName: '',
    city: ''
  })
  const [profileImageUrl, setProfileImageUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [externalArtistData, setExternalArtistData] = useState<ExternalArtist | null>(null)

  useEffect(() => {
    // If we have a username, try to fetch data from external source
    if (initialUsername) {
      fetchExternalArtistData(initialUsername)
    }
  }, [initialUsername])

  const checkArtistExists = async (username: string): Promise<{ exists: boolean, type?: 'real' | 'unreal' }> => {
    try {
      // Check real artists collection
      const realArtistsQuery = query(
        collection(db, 'artists'), 
        where('displayName', '==', username)
      )
      const realArtistsSnapshot = await getDocs(realArtistsQuery)
      
      if (!realArtistsSnapshot.empty) {
        return { exists: true, type: 'real' }
      }

      // Check unreal artists collection
      const unrealArtistsQuery = query(
        collection(db, 'unrealArtists'),
        where('username', '==', username)
      )
      const unrealArtistsSnapshot = await getDocs(unrealArtistsQuery)
      
      if (!unrealArtistsSnapshot.empty) {
        return { exists: true, type: 'unreal' }
      }

      return { exists: false }
    } catch (error) {
      console.error('Error checking if artist exists:', error)
      return { exists: false }
    }
  }

  const fetchExternalArtistData = async (username: string) => {
    try {
      const jsonFileRef = ref(storage, 'simplified_tattoo_artists.json')
      const fileBytes = await getBytes(jsonFileRef)
      const jsonString = new TextDecoder().decode(fileBytes)
      const externalArtists = JSON.parse(jsonString) as ExternalArtist[]
      
      const foundArtist = externalArtists.find(artist => 
        artist.username.toLowerCase() === username.toLowerCase()
      )
      
      if (foundArtist) {
        setExternalArtistData(foundArtist)
        setFormData({
          username: foundArtist.username,
          displayName: foundArtist.username,
          city: foundArtist.city || ''
        })
        setProfileImageUrl(foundArtist.profile_image_url || '')
      }
    } catch (error) {
      console.error('Error fetching external artist data:', error)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!user) {
      setShowAuthModal(true)
      return
    }

    if (!formData.username.trim()) {
      setValidationError('Username is required')
      return
    }

    try {
      setLoading(true)
      setValidationError(null)

      // Check if artist already exists
      const artistCheck = await checkArtistExists(formData.username.trim())
      if (artistCheck.exists) {
        setValidationError(`Artist "${formData.username.trim()}" already exists${artistCheck.type === 'real' ? ' as a verified artist' : ''}. Try searching for them instead.`)
        setLoading(false)
        return
      }

      const newArtist: Omit<UnrealArtist, 'id'> = {
        username: formData.username.trim(),
        displayName: formData.displayName.trim() || formData.username.trim(),
        profileImageUrl: profileImageUrl || undefined,
        biography: '',
        city: formData.city.trim() || undefined,
        createdAt: new Date(),
        averageRating: undefined,
        totalReviews: 0,
        categoryRatings: undefined
      }

      // Use username as document ID for consistency
      const artistRef = doc(db, 'unrealArtists', formData.username.trim())
      await setDoc(artistRef, {
        ...newArtist,
        createdAt: serverTimestamp()
      })

      navigate(`/rate-my-artist/rate/unreal/${formData.username.trim()}`)
    } catch (error) {
      console.error('Error creating artist:', error)
      // TODO: Show error toast
    } finally {
      setLoading(false)
    }
  }

  const handleUsernameChange = (value: string) => {
    setFormData(prev => ({ ...prev, username: value }))
    setValidationError(null) // Clear validation error when user types
  }

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <>
      <div className="min-h-screen bg-background" style={{ paddingBottom: 'var(--bottom-nav-offset)' }}>
        <div className="phone-container-padded pt-8">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="p-2"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-semibold">Add New Artist</h1>
              <p className="text-sm text-muted-foreground">
                Create a profile to rate and review
              </p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Artist Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Profile Preview */}
                {(profileImageUrl || formData.username) && (
                  <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
                    {profileImageUrl ? (
                      <img
                        src={profileImageUrl}
                        alt={formData.username}
                        className="w-16 h-16 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-muted-foreground/20 flex items-center justify-center">
                        <span className="text-lg font-medium">
                          {formData.username[0]?.toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="font-semibold">@{formData.username}</p>
                      {formData.displayName && formData.displayName !== formData.username && (
                        <p className="text-sm text-muted-foreground">{formData.displayName}</p>
                      )}
                      {formData.city && (
                        <p className="text-sm text-muted-foreground">{formData.city}</p>
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <Label htmlFor="username">Instagram Username *</Label>
                  <Input
                    id="username"
                    type="text"
                    placeholder="username (without @)"
                    value={formData.username}
                    onChange={(e) => handleUsernameChange(e.target.value)}
                    required
                    className={`mt-1 ${validationError ? 'border-red-500 focus:border-red-500' : ''}`}
                  />
                  {validationError && (
                    <p className="text-xs text-red-500 mt-1">
                      {validationError}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    Enter the artist's Instagram username without the @ symbol
                  </p>
                </div>

                <div>
                  <Label htmlFor="displayName">Display Name</Label>
                  <Input
                    id="displayName"
                    type="text"
                    placeholder="Artist's full name (optional)"
                    value={formData.displayName}
                    onChange={(e) => handleInputChange('displayName', e.target.value)}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="city" className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    City
                  </Label>
                  <Input
                    id="city"
                    type="text"
                    placeholder="City where the artist is based"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    className="mt-1"
                  />
                </div>

              </CardContent>
            </Card>

            {externalArtistData && (
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-4">
                  <p className="text-sm text-green-800">
                    ✓ Artist data found and pre-filled from our database
                  </p>
                </CardContent>
              </Card>
            )}

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading || !formData.username.trim()}
                className="flex-1"
              >
                {loading ? 'Creating...' : 'Create Artist Profile'}
              </Button>
            </div>
          </form>

          {searchQuery && (
            <div className="mt-6 p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">
                Searched for: <span className="font-medium">"{searchQuery}"</span>
              </p>
            </div>
          )}
        </div>
      </div>

      <AuthModal 
        isOpen={showAuthModal} 
        onOpenChange={(open) => setShowAuthModal(open)} 
      />
    </>
  )
}
